import { inject, injectable } from 'inversify';
import React from 'react';

import { User } from '@project44/ui-utils';
import {
  DtoBase,
  SharedModels,
  UserModels,
} from '@project44-models/iam-gateway';

import { TFunctionComponent } from '../../../../shared/core/infrastructrue/types';
import { UnifiedRolesDomainAbstractions } from '../../../UnifiedRoles';
import { UnifiedRolesEditorResolver } from '../../../UnifiedRoles/domain/policies/UnifiedRolesEditorResolver';
import { UserFormResolver } from '../../domain/forms/UserForm.resolver';
import {
  IUserUIFactory,
  TAssignedUserTenantAgnosticRolesState,
  TAssignedUserTenantSpecificRolesState,
  TAvailableTenantAgnosticRolesState,
  TUserMoveFormComponent,
  TUserTenantAgnosticRolesCheckbox,
  TUserTenantAgnosticRolesSelectAllCheckbox,
  TUserTenantSpecificRolesCheckbox,
  TUserTenantSpecificRolesSelectAllCheckbox,
  TUseUserCreator,
} from '../../domain/interfaces';
import { UserCreationFormComponent } from '../ui/UserCreatorForm/UserCreationForm.component';
import { TUserCreationFormView } from '../ui/UserCreatorForm/UserCreationForm.view';
import { UserMoveForm } from '../ui/UserMoveForm/UserMoveForm';
import { UserMoveFormComponent } from '../ui/UserMoveForm/UserMoveForm.component';
import { UserTenantAgnosticRolesCheckbox } from '../ui/UserTenantAgnosticRolesCheckbox/UserTenantAgnosticRolesCheckbox.component';
import { UserTenantAgnosticRolesSelectAllCheckbox } from '../ui/UserTenantAgnosticRolesSelectAllCheckbox/UserTenantAgnosticRolesSelectAllCheckbox.component';
import { UserTenantSpecificRolesCheckbox } from '../ui/UserTenantSpecificRolesCheckbox/UserTenantSpecificRolesCheckbox.component';
import { UserTenantSpecificRolesSelectAllCheckbox } from '../ui/UserTenantSpecificRolesSelectAllCheckbox/UserTenantSpecificRolesSelectAllCheckbox.component';
import { UserUnifiedRolesTable } from '../ui/UserUnifiedRolesTable/UserUnifiedRolesTable.component';
import { UserFactory } from './User.factory';
import { UserHookFactory } from './User.hook.factory';

@injectable()
export class UserComponentFactory implements IUserUIFactory {
  constructor(
    @inject(UserFactory) private readonly userFactory: UserFactory,
    @inject(UserFormResolver)
    private readonly userFormResolver: UserFormResolver,

    @inject(UserHookFactory)
    private readonly userHookFactory: UserHookFactory,
  ) {}

  public readonly composeUserUnifiedRolesTable = (): UserUnifiedRolesTable => {
    return UserUnifiedRolesTable.create({
      composeUserTenantSpecificRolesCheckbox:
        this.composeUserTenantSpecificRolesCheckbox,
      composeUserTenantAgnosticRolesCheckbox:
        this.composeUserTenantAgnosticRolesCheckbox,
      composeUserTenantSpecificRolesSelectAllCheckbox:
        this.composeUserTenantSpecificRolesSelectAllCheckbox,
      composeUserTenantAgnosticRolesSelectAllCheckbox:
        this.composeUserTenantAgnosticRolesSelectAllCheckbox,
      shouldDisableNMCRolesSupport:
        UnifiedRolesEditorResolver.shouldDisableNMCRolesSupport,
    });
  };

  public readonly composeUserTenantSpecificRolesCheckbox = (payload: {
    isApiRequestInProgress: boolean;
    userPrincipal: User;
    unifiedRole: SharedModels.UnifiedRoleViewModel;
    assignedUserTenantSpecificRolesState: TAssignedUserTenantSpecificRolesState;
    availableTenantAgnosticRoles: TAvailableTenantAgnosticRolesState[0];
    userTenantSpecificRolesEditor: UnifiedRolesDomainAbstractions.UnifiedRolesEditorPolicy;
  }): React.ReactElement<TUserTenantSpecificRolesCheckbox> => {
    return UserTenantSpecificRolesCheckbox.create(payload);
  };

  public readonly composeUserTenantAgnosticRolesCheckbox = (payload: {
    isApiRequestInProgress: boolean;
    userPrincipal: User;
    unifiedRole: SharedModels.UnifiedRoleViewModel;
    assignedUserTenantAgnosticRolesState: TAssignedUserTenantAgnosticRolesState;
    userTenantAgnosticRolesEditor: UnifiedRolesDomainAbstractions.UnifiedRolesEditorPolicy;
  }): React.ReactElement<TUserTenantAgnosticRolesCheckbox> => {
    return UserTenantAgnosticRolesCheckbox.create(payload);
  };

  public readonly composeUserTenantSpecificRolesSelectAllCheckbox = (payload: {
    isApiRequestInProgress: boolean;
    userPrincipal: User;
    assignedUserTenantSpecificRolesState: TAssignedUserTenantSpecificRolesState;
    availableTenantAgnosticRoles: TAvailableTenantAgnosticRolesState[0];
    userTenantSpecificRolesEditor: UnifiedRolesDomainAbstractions.UnifiedRolesEditorPolicy;
  }): React.ReactElement<TUserTenantSpecificRolesSelectAllCheckbox> => {
    return UserTenantSpecificRolesSelectAllCheckbox.create(payload);
  };

  public readonly composeUserTenantAgnosticRolesSelectAllCheckbox = (payload: {
    isApiRequestInProgress: boolean;
    userPrincipal: User;
    assignedUserTenantAgnosticRolesState: TAssignedUserTenantAgnosticRolesState;
    userTenantAgnosticRolesEditor: UnifiedRolesDomainAbstractions.UnifiedRolesEditorPolicy;
  }): React.ReactElement<TUserTenantAgnosticRolesSelectAllCheckbox> => {
    return UserTenantAgnosticRolesSelectAllCheckbox.create(payload);
  };

  public readonly composeUserCreationForm = (payload: {
    userPrincipal: User;
    useUserCreatorConfig: ReturnType<TUseUserCreator>;
    afterUserCreationActionHandler?: (
      user: DtoBase<UserModels.UserViewModel>,
    ) => void;
  }): React.ReactElement<TUserCreationFormView> => {
    const {
      userPrincipal,
      afterUserCreationActionHandler,
      useUserCreatorConfig,
    } = payload;

    const userCreationFormTemplate =
      this.userFormResolver.resolveUserCreationFormTemplate({
        userPrincipal,
      });

    return UserCreationFormComponent.create({
      userPrincipal,
      userCreationFormTemplate,
      afterUserCreationActionHandler,
      useUserCreatorConfig,
    });
  };

  public readonly composeUserMoveForm: TFunctionComponent<
    Omit<TUserMoveFormComponent, 'UserMoveFormCreator'>
  > = (props) => {
    const userCreationFormTemplate =
      this.userFormResolver.resolveUserMoveFormTemplate();

    return UserMoveFormComponent.create({
      ...props,
      UserMoveFormCreator: UserMoveForm.compose(userCreationFormTemplate),
    });
  };
}
